import { History, RotateCcw, X } from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { useEffect, useState } from "react";

import { useT } from "@/i18n";
import {
  draftsApi,
  type DraftHistoryEntry,
} from "@/lib/builder-api";
import { formatRelative } from "@pervasivemind/dashboard-render";

interface HistoryPanelProps {
  /** Draft key — scopes the history endpoint. */
  draftKey: string;
  /** Toggle from the parent header button. */
  open: boolean;
  /** Close handler — fires on backdrop click, ESC, or the X button. */
  onClose: () => void;
  /** Fired after a successful revert so the parent can reload the draft. */
  onReverted: () => void;
}

/**
 * Right-side sliding panel that lists the draft's pre-update snapshots
 * and lets the admin revert to any of them. Each snapshot shows:
 *
 *   - relative timestamp ("hace 3 minutos")
 *   - created_by user ID (when recorded)
 *   - a simple stat line (sections, KPIs, charts, filters) derived from
 *     the snapshot's dashboard_config so the user can tell snapshots
 *     apart without diffing raw JSON.
 *
 * Backend: ``GET /api/builder/{key}/history`` — wired in ``builderApi``.
 * Revert:  ``POST /api/builder/{key}/revert/{history_id}`` writes a new
 * history row (the current state pre-revert) so the timeline remains a
 * complete audit trail.
 */
export function HistoryPanel({
  draftKey,
  open,
  onClose,
  onReverted,
}: HistoryPanelProps): JSX.Element | null {
  const { t } = useT();
  const [entries, setEntries] = useState<DraftHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reverting, setReverting] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !draftKey) return;
    setLoading(true);
    setError(null);
    draftsApi
      .history(draftKey)
      .then((rows) => setEntries(rows))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Failed to load history"),
      )
      .finally(() => setLoading(false));
  }, [open, draftKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleRevert = async (historyId: string): Promise<void> => {
    if (!window.confirm(t("history.revertConfirm"))) return;
    setReverting(historyId);
    try {
      await draftsApi.revert(draftKey, historyId);
      onReverted();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Revert failed");
    } finally {
      setReverting(null);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="history-panel-title"
        className="fixed right-0 top-0 z-50 flex h-full w-96 flex-col border-l border-neutral-200 bg-white shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <History size={16} className="text-primary" />
            <h2
              id="history-panel-title"
              className="text-sm font-semibold text-neutral-900"
            >
              {t("history.title")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
            title={t("preview.close")}
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-3">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-500">
              <Spinner size={16} ariaLabel={t("common.loading")} />
              {t("common.loading")}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-error/40 bg-error/5 px-3 py-2 text-xs text-error-dark">
              {error}
            </div>
          )}
          {!loading && !error && entries.length === 0 && (
            <p className="py-10 text-center text-xs text-neutral-500">
              {t("history.empty")}
            </p>
          )}
          {!loading && entries.length > 0 && (
            <ul className="space-y-2">
              {entries.map((entry) => (
                <HistoryEntryCard
                  key={entry.id}
                  entry={entry}
                  reverting={reverting === entry.id}
                  onRevert={() => void handleRevert(entry.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}

interface HistoryEntryCardProps {
  entry: DraftHistoryEntry;
  reverting: boolean;
  onRevert: () => void;
}

function HistoryEntryCard({
  entry,
  reverting,
  onRevert,
}: HistoryEntryCardProps): JSX.Element {
  const { t } = useT();
  const stats = summarizeSnapshot(entry);
  return (
    <li className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-neutral-800">
            {formatRelative(entry.created_at)}
          </p>
          {entry.created_by && (
            <p className="truncate text-xs text-neutral-500 font-mono">
              {entry.created_by}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onRevert}
          disabled={reverting}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {reverting ? (
            <Spinner size={12} ariaLabel={t("history.revert")} />
          ) : (
            <RotateCcw size={11} />
          )}
          {t("history.revert")}
        </button>
      </div>
      <dl className="mt-2 grid grid-cols-4 gap-1 text-center">
        <StatBadge label={t("history.stat.sections")} value={stats.sections} />
        <StatBadge label={t("history.stat.kpis")} value={stats.kpis} />
        <StatBadge label={t("history.stat.charts")} value={stats.charts} />
        <StatBadge label={t("history.stat.filters")} value={stats.filters} />
      </dl>
    </li>
  );
}

function StatBadge({
  label,
  value,
}: {
  label: string;
  value: number;
}): JSX.Element {
  return (
    <div className="rounded border border-neutral-200 bg-white px-1.5 py-1">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className="text-xs font-semibold tabular-nums text-neutral-800">
        {value}
      </div>
    </div>
  );
}

/** Cheap derived stats so snapshots are distinguishable at a glance. */
function summarizeSnapshot(entry: DraftHistoryEntry): {
  sections: number;
  kpis: number;
  charts: number;
  filters: number;
} {
  const dash = entry.dashboard_config as {
    sections?: Array<{ items?: Array<{ element?: string }> }>;
    global_filters?: unknown[];
  };
  const sections = dash.sections ?? [];
  let kpis = 0;
  let charts = 0;
  for (const s of sections) {
    for (const it of s.items ?? []) {
      if (it.element === "kpi") kpis++;
      else if (it.element === "chart") charts++;
    }
  }
  return {
    sections: sections.length,
    kpis,
    charts,
    filters: (dash.global_filters ?? []).length,
  };
}
